import React, { useCallback, useEffect, useState } from 'react';
import { DividerPadding, OverAllContainer, ScenerioModellingContainer, SubTitle, Title, VisualScenerioFlex } from './ScenerioModelling.styles';
import Footer from '../Footer/Footer';
import ScenerioModellingChart from '../charts/ScenerioModellingChart';
import { fetchNetCarbonIntensity, fetchbasecaseOverallPortfolio, fetchbasecasetabledata, getImportedDeals } from '../../api/services';
import Loader from '../Loader/Loader';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { showSpinner, hideSpinner } from '../../redux/datavisualization/datavisualizationSlice';
import { ALL_COUNTRIES, ALL_REGIONS, UNITED_STATES } from '../../api/constants';
import { BUSINESS_PLAN_KEY, DEAL_FUNNEL_KEY, exampleData, one, REGIONS, SCENERIO_MODELLING_SUB_TITLE, SCENERIO_MODELLING_TITLE } from '../../constants';
import {
  Area,
  ChartData,
  CommodityDataEntry,
  IGroupNCI,
  ImportedDeals,
  IProduct,
  SubRegion,
  TableData,
  TotalPlanData,
} from '../../Types/types';
import {
  calculateCountryData,
  getNetCarbonIntensity,
  getTransformedTableData,
  revertNciChartData,
  transformRegionData,
  getDealFunnelOptions,
  getBusinessPlanOptions,
  getSelectedRegionData,
  getNciChartData,
} from './util1';
import NetCarbonIntensity from '../charts/NetCarbonIntensity/NetCarbonIntensity';
import ScenarioModellingTabs from './ScenarioModellingTabs/ScenarioModellingTabs';
import { setOutPlanDealsId, setIsReset, setSelectedAddToChartDeals, setTabKey } from '../../redux/dashboard/dashboardSlice';
import { formatRegionName } from '../../utils';
import {  generateScenarioChartData, getUpdatedCommodityData, getUpdatedRegionData, generateScenarioAndNciDataOnValueChange, generateNetCarbonIntensityChartData } from './util2';
import { FETCH_ERROR } from '../../utils/constants';
import { notify } from '../Toast/notify';
import { globalApiErrorHandler } from '../../../Tanso/components/Utils/utils';

const ScenerioModelling: React.FC = () => {
  const [selectedRegion, setSelectedRegion] = useState(ALL_REGIONS);
  const [selectedSubRegion, setSelectedSubRegion] = useState(UNITED_STATES);
  const [subRegionOptions, setSubRegionOptions] = useState<SubRegion[]>([]);
  const [regionData, setRegionData] = useState<IProduct[] | undefined>(undefined);
  const [masterRegionData, setMasterRegionData] = useState<IProduct[] | undefined>(undefined);
  const [allRegionData, setAllRegionData] = useState<IProduct[]>();
  const [scenerioChartData, setScenerioChartData] = useState<Area[]>(exampleData);
  const [nciTransformData, setNciTransformData] = useState<Area[]>(exampleData);
  const [mergedOverllPortfolio, setMergedOverllPortfolio] = useState<CommodityDataEntry[]>([]);
  const [importedDealApiCalled, setImportedDealApiCalled] = useState<boolean>(false);
  const [addedImportedDealsData, setAddedImportedDealsData] = useState<ImportedDeals[]>([]);
  const [inPlanDealsId, setInPlanDealsId] = useState<number[]>([]);
  const dispatch = useAppDispatch();
  const { nciData, importedDeals, outPlanDealsId, selectedAddToChartDeals, tabKey, userRegionFlags, loggedInUserRegion, userEvp, historicalStartYear, planningEndYear } = useAppSelector((state) => state.dashboard);
  const [netCarbonIntensityData, setNetCarbonIntensityData] = useState<IGroupNCI[]>(nciData);
  const [importedDealsData, setImportedDealsData] = useState<ImportedDeals[]>(importedDeals);
  const [totalPlanDatas, setTotalPlanDatas] = useState<TotalPlanData[]>([]);
  const [prevTabKey, setPrevTabKey] = useState<string>('');

  const fetchsceneriodata = async (isReset = false) => {
    dispatch(showSpinner());
    try {
      const [carbonintensityTargetResp, tableDataResp] = await Promise.all([
        fetchbasecaseOverallPortfolio(loggedInUserRegion, userEvp),
        fetchbasecasetabledata(REGIONS, userEvp),
      ]);
      if (!nciData.length) {
        const carbonIntensity = await fetchNetCarbonIntensity(loggedInUserRegion, userEvp);
        if (carbonIntensity) {
          const { data } = carbonIntensity;
          const filterData = data.filter((item: IGroupNCI) => item.YEAR >= historicalStartYear && item.YEAR <= planningEndYear);
          setNetCarbonIntensityData(filterData);
        }
      }
      if (carbonintensityTargetResp) {
        const { data } = carbonintensityTargetResp;
        setMergedOverllPortfolio(data);
        scenarionChart(data, [], isReset);
      }
      if (tableDataResp) {
        const tableData = tableDataResp?.data;
        const sortedData: TableData[] = tableData?.reduce(transformRegionData, []);
        const transformedTableData = getTransformedTableData(sortedData);
        setRegionData(transformedTableData);
        setMasterRegionData(transformedTableData);
        if (transformedTableData) {
          handleTabChange(loggedInUserRegion ?? ALL_REGIONS);
        }
      }
    } catch (error) {
      globalApiErrorHandler('Error fetching scenario modelling data', error)
      notify('error', FETCH_ERROR);
    } finally {
      dispatch(hideSpinner());
    }
  };

  useEffect(() => {
    (async () => {
      if (!importedDealsData.length && !importedDealApiCalled) {
        const importedDealsRes = await getImportedDeals(loggedInUserRegion, userEvp);
        importedDealsRes && setImportedDealsData(importedDealsRes?.data);
        setImportedDealApiCalled(true);
      }
    })();
  }, [importedDealsData, importedDealApiCalled, loggedInUserRegion, userEvp]);

  const initialNciChartData = useCallback(() => {
    const netCarbonIntensity = getNetCarbonIntensity(
      netCarbonIntensityData as unknown as IGroupNCI[],
    );
    setNciTransformData([...netCarbonIntensity]);
  }, [netCarbonIntensityData]);

  useEffect(() => {
    initialNciChartData();
  }, [netCarbonIntensityData, initialNciChartData]);

  const scenarionChart = (
    overAllPortfoliodata: CommodityDataEntry[],
    importedDealsScenario: ImportedDeals[],
    isReset = false
  ) => {
    const { tempTotalPlansData, transformedData, transformedbudgetlineData } = generateScenarioChartData({
      overAllPortfoliodata,
      importedDealsScenario,
      isReset,
      loggedInUserRegion,
      selectedRegion,
      totalPlanDatas,
      historicalStartYear,
      planningEndYear,
    });
    tempTotalPlansData && setTotalPlanDatas(tempTotalPlansData);
    transformedData && setScenerioChartData([...transformedData, transformedbudgetlineData]);
  };

  useEffect(() => {
    fetchsceneriodata();
    dispatch(setTabKey(BUSINESS_PLAN_KEY));
  }, []);

  useEffect(() => {
    const { localInPlanDealsId, filteredDeals, updatedFilter } = generateNetCarbonIntensityChartData({
      inPlanDealsId,
      selectedAddToChartDeals,
      outPlanDealsId,
      importedDealsData,
    });
    if (selectedAddToChartDeals.IS_OPERATING_PLAN === one) {
      setInPlanDealsId(localInPlanDealsId);
    }
    filteredDeals.length
      ? setNciTransformData(getNciChartData(filteredDeals, nciTransformData, selectedAddToChartDeals))
      : initialNciChartData();
    setAddedImportedDealsData(updatedFilter);
  }, [outPlanDealsId.length, selectedAddToChartDeals]);

  useEffect(() => {
    const sampleDataAdded = regionData && calculateCountryData(regionData);

    if (sampleDataAdded) {
      const sortedData = sampleDataAdded.map((data: IProduct) => {
        const sortedCategoryData = [...data.categoryData].sort((a, b) =>
          a.category.localeCompare(b.category),
        );
        return { ...data, categoryData: sortedCategoryData };
      });
      setAllRegionData(sortedData);
    }
  }, [regionData]);

  const handleTabChange = (region: string) => {
    const subregions =
      tabKey === DEAL_FUNNEL_KEY
        ? getDealFunnelOptions(importedDealsData, region)
        : getBusinessPlanOptions(masterRegionData as IProduct[], region, userRegionFlags);
    if (subregions) {
      const defaultSubRegion = tabKey === BUSINESS_PLAN_KEY ? formatRegionName(subregions[0].value) : ALL_COUNTRIES;
      setSubRegionOptions(subregions);
      setSelectedSubRegion(defaultSubRegion);
      setSelectedRegion(region);
    }
  };

  const handleValueChange = (
    region: string,
    subregion: string,
    category: string,
    product: string,
    productId: string,
    year: number,
    newValue: number,
  ): void => {
    const updatedNciAndScenarioData = generateScenarioAndNciDataOnValueChange({
      regionData: regionData as IProduct[],
      region,
      subregion,
      productId,
      year,
      category,
      product,
      newValue,
      nciTransformData,
      scenerioChartData
    })
    if (updatedNciAndScenarioData) {
      const { updatedNciData, updatedScenarioChartData, updatedRegionData } = updatedNciAndScenarioData;
      setNciTransformData(updatedNciData);
      setScenerioChartData(updatedScenarioChartData as Area[]);
      setRegionData(updatedRegionData);
    } else {
      return undefined;
    }
  };

  const handleRevertToOriginal = (
    region: string,
    subregion: string,
    category: string,
    product: string,
    productId: string,
    year: number,
  ) => {
    const revertedData = revertNciChartData(
      nciTransformData,
      year,
      regionData,
      region,
      subregion,
      productId,
    );
    setNciTransformData(revertedData as Area[]);
    const updatedRegionData = getUpdatedRegionData({
      regionData,
      masterRegionData,
      region,
      subregion,
      category,
      productId,
      year,
      scenerioChartData,
      product,
      setScenerioChartData
    });

    setRegionData(updatedRegionData);
  };

  const selectedRegionData = getSelectedRegionData(
    regionData as IProduct[],
    allRegionData as IProduct[],
    selectedRegion,
    selectedSubRegion,
  );

  const handleValueChangeWrapper = (
    rowId: string,
    category: string,
    product: string,
    year: number,
    newValue: number,
  ) => {
    handleValueChange(selectedRegion, selectedSubRegion, category, product, rowId, year, newValue);
  };

  const handleRevertToOriginalWrapper = (
    rowId: string,
    category: string,
    product: string,
    year: number,
  ) => {
    handleRevertToOriginal(selectedRegion, selectedSubRegion, category, product, rowId, year);
  };

  const handleRevertAllToOriginal = () => {
    handleTabChange(loggedInUserRegion ?? ALL_REGIONS);
    setRegionData(masterRegionData);
    initialNciChartData();
    if (mergedOverllPortfolio.length) {
      scenarionChart(mergedOverllPortfolio, [], true);
    }
    dispatch(setIsReset(true));
    setAddedImportedDealsData([]);
    dispatch(setSelectedAddToChartDeals({} as ImportedDeals));
    setInPlanDealsId([]);
    dispatch(setOutPlanDealsId([]));
  }

  useEffect(() => {
    if (tabKey === BUSINESS_PLAN_KEY) {
      handleRevertAllToOriginal();
    }
    setPrevTabKey(tabKey);
  }, [tabKey]);

  useEffect(() => {
    const updatedData = getUpdatedCommodityData({
      selectedRegion,
      mergedOverllPortfolio,
      regionData: regionData as IProduct[],
      selectedRegionData: selectedRegionData as IProduct
    });
    const shouldUpdateChart = tabKey !== BUSINESS_PLAN_KEY || prevTabKey !== DEAL_FUNNEL_KEY;
    if (updatedData && shouldUpdateChart) {
      scenarionChart(updatedData, addedImportedDealsData);
    }
  }, [selectedRegion, addedImportedDealsData.length]);

  return (
    <>
      <ScenerioModellingContainer>
        <Loader data-testid='loader' />
        <div>
          <Title>{SCENERIO_MODELLING_TITLE}</Title>
          <SubTitle>{SCENERIO_MODELLING_SUB_TITLE}</SubTitle>
        </div>
        <VisualScenerioFlex>
          <OverAllContainer>
            <ScenerioModellingChart
              scenerioModellingdata={scenerioChartData as ChartData[]}
              chartType='scenerio'
            />
          </OverAllContainer>
          <OverAllContainer>
            <NetCarbonIntensity netCarbonIntensity={nciTransformData as ChartData[]} />
          </OverAllContainer>
        </VisualScenerioFlex>
        <DividerPadding />
        <ScenarioModellingTabs
          selectedRegion={selectedRegion}
          handleTabChange={handleTabChange}
          subRegionOptions={subRegionOptions}
          selectedSubRegion={selectedSubRegion}
          setSubRegionOptions={setSubRegionOptions}
          setSelectedSubRegion={setSelectedSubRegion}
          setSelectedRegion={setSelectedRegion}
          handleRevertAllToOriginal={handleRevertAllToOriginal}
          selectedRegionData={selectedRegionData as IProduct}
          handleValueChangeWrapper={handleValueChangeWrapper}
          handleRevertToOriginalWrapper={handleRevertToOriginalWrapper}
          importedDealsData={importedDealsData}
          regionData={regionData as IProduct[]}
        />
      </ScenerioModellingContainer>
      <Footer />
    </>
  );
};

export default ScenerioModelling;
